import React, { useContext, useEffect, useState } from 'react';
import { useNavigate } from "react-router-dom";
import { UserContext } from "../UserContext";
import { hasAnyPerm } from '../utils/uiUtils';
import { Button, Card, Col, Container, Dropdown, DropdownButton, Form, Row } from "react-bootstrap";
import { PencilIcon } from '@primer/octicons-react'
import { axiosAuth_PLATFORM_API } from '../utils/AxoisUtilComponent';
import { useTranslation } from 'react-i18next';
import classes from './AdminPages.module.css';
import { translateAPIError } from '../utils/apiUtils';
import { formatDate } from '../utils/timeUtils';
import DOMPurify from 'dompurify';


const MsgTmplAdminDetailsView = (props) => {

  // Utilities
  const { user } = useContext(UserContext);
  const { t } = useTranslation(['common', 'custom']);
  const navigate = useNavigate();

  // Initialize with actual JSON obj.  If not, will get error.
  const [objAttrs, setObjAttrs] = useState({
    id: '',
    name: '',
    description: '',
    type: '',
    language: '',
    subject: '',
    body: '',
    updatedBy: '',
    timeUpdated: '',
    createdBy: '',
    timeCreated: ''
  });

  const [currentObjAttrs, setCurrentObjAttrs] = useState([]);

  const [languageList, ] = useState([
    { key: "en",    value: "en" },
    { key: "zh_HK", value: "zh_HK" },
    { key: "zh_CN", value: "zh_CN" }
  ]);

  const [typeList, ] = useState([
    { key: "EMAIL_TMPL", value: "EMAIL_TMPL" },
    { key: "SMS_TMPL",   value: "SMS_TMPL" }
  ]);


  // UI Functions

  // Initialize state
  useEffect(() => {
    if (props) {
      let tmpAttrs = JSON.parse(JSON.stringify(props.objDetails));

      tmpAttrs.name    = props.objDetails.name.substring( 0, props.objDetails.name.indexOf( '_' + props.objDetails.language) );
      tmpAttrs.subject = (props.objDetails.subject && props.objDetails.subject.length > 0) ?
                         decodeURIComponent(props.objDetails.subject) : '';
      tmpAttrs.body    = (props.objDetails.body && props.objDetails.body.length > 0) ?
                         decodeURIComponent(props.objDetails.body) : '';

      setObjAttrs(tmpAttrs);
      setCurrentObjAttrs(tmpAttrs);
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [props]);


  const fetchMsgTmpl = (id) => {

    return new Promise(async (resolve, reject) => {
      try {
        const res = await axiosAuth_PLATFORM_API.get("msg-templates/" + id);

        resolve(res.data);

        if (res.status === 200) {
          let tmpAttrs = JSON.parse(JSON.stringify(res.data[0]));

          tmpAttrs.name    = tmpAttrs.name.substring( 0, tmpAttrs.name.indexOf( '_' + tmpAttrs.language) );
          tmpAttrs.subject = (tmpAttrs.subject && tmpAttrs.subject.length > 0) ?
                             decodeURIComponent(tmpAttrs.subject) : '';
          tmpAttrs.body    = (tmpAttrs.body && tmpAttrs.body.length > 0) ?
                             decodeURIComponent(tmpAttrs.body) : '';

          setObjAttrs(tmpAttrs);
          setCurrentObjAttrs(tmpAttrs);
        }

        setErrMsg('');
      } catch (error) {
        setErrMsg(t(translateAPIError(error)));
      }
    });
  }

  // View vs Edit Modes
  const [editMode,    setEditMode]    = useState(false);
  const [previewMode, setPreviewMode] = useState(false);

  const [errMsg, setErrMsg] = useState('');

  const handleEditAttrs = (e) => {
    e.preventDefault();
    setEditMode(true);
  }


  const onAttrsInputChange = (e) => {
    setObjAttrs({ ...objAttrs, [e.target.name]: e.target.value })
  }

  const onLanguageSelect = (key, event) => {
    setObjAttrs({ ...objAttrs, language: key })
  };

  const onTypeSelect = (key, event) => {
    setObjAttrs({ ...objAttrs, type: key })
  };

  const handleEditCancel = (e) => {
    e.preventDefault();

    // Reset fields
    setObjAttrs(currentObjAttrs);

    setEditMode(false);
  }

  const handleEditSubmit = (e) => {
    e.preventDefault();

    // Validate form inputs
    if (objAttrs.name.length === 0) {
      setErrMsg(t("I18N_ERR_Need_name"));
    } else if ((objAttrs.name != null) && (objAttrs.name.length > 100)) {
      setErrMsg(t("I18N_ERR_Max_len_name") + 100);

    } else if (objAttrs.description === '') {
      setErrMsg(t("I18N_ERR_Need_description"));
    } else if ((objAttrs.description != null) && (objAttrs.description.length > 250)) {
      setErrMsg(t("I18N_ERR_Max_len_desc") + 250);

    } else if (objAttrs.language === '') {
      setErrMsg(t("I18N_ERR_Need_language"));
    } else if ((objAttrs.language != null) && (objAttrs.type.language > 10)) {
      setErrMsg(t("I18N_ERR_Max_len_lang") + 10);

    } else if (objAttrs.type === '') {
      setErrMsg(t("I18N_ERR_Need_type"));
    } else if ((objAttrs.type != null) && (objAttrs.type.length > 20)) {
      setErrMsg(t("I18N_ERR_Max_len_type") + 20);

    } else if (objAttrs.subject === '') {
      setErrMsg(t("I18N_ERR_Need_subject"));
    } else if ((objAttrs.subject != null) && (objAttrs.subject.length > 200)) {
      setErrMsg(t("I18N_ERR_Max_len_subject") + 200);

    } else if (objAttrs.body === '') {
      setErrMsg(t("I18N_ERR_Need_body"));
    } else if ((objAttrs.body != null) && (objAttrs.body.length > 2000)) {
      setErrMsg(t("I18N_ERR_Max_len_body") + 2000);

    } else {
      setErrMsg('');
      updateObj();
    }
  }

  const updateObj = () => {

    // Deep copy
    let tmpAttrs = JSON.parse(JSON.stringify(objAttrs));

    tmpAttrs.name    = tmpAttrs.name + '_' + tmpAttrs.language;
    tmpAttrs.subject = encodeURIComponent(tmpAttrs.subject);
    tmpAttrs.body    = encodeURIComponent(tmpAttrs.body);

    const reqBody = tmpAttrs;

    return new Promise(async (resolve, reject) => {
      try {
        const res = await axiosAuth_PLATFORM_API.put("msg-templates/" + objAttrs.id, reqBody);

        resolve(res.data);

        if (res.status === 200) {

          // Restore UI to viewing mode
          setEditMode(false);

          fetchMsgTmpl (tmpAttrs.id);
        }
      } catch (error) {
        if (!error.message || (error.message !== 'IGNORE')) {
          setErrMsg(t(translateAPIError(error)));
        }
      }
    });

  }

  const togglePreview = (e) => {
    e.preventDefault();
    setPreviewMode(!previewMode);
  }


  const handleClose = (e) => {
    e.preventDefault();

    // Return to list
    navigate(-1);
  }


  return (
    <>
      <Card style={{overflow:'auto', border:'0', boxShadow:'0 2px 2px 0px rgba(0,0,0,0.1)',
                    padding:'20px 40px 20px 40px', maxWidth:'2500px', width:'100%', fontSize:'13px'}}>

        <div style={{display:'flex'}}>
        <span className={classes.admin_pages__page_title}>
          {t("I18N_MsgTmpl_Details")}
        </span>
        </div>

        <div className={classes.admin_pages__page_subtitle} style={{marginTop:'20px'}}>
          <div display='flex'>
            <span>
              {t("I18N_MsgTmpl_info")}
            </span>
            {editMode ?
              null
              :
              <span style={{marginLeft:'15px', fontSize:'9px', color:'SteelBlue', cursor:'pointer'}} onClick={handleEditAttrs}>
                <PencilIcon size={12} />
                {t("I18N_Edit")}
              </span>
            }
          </div>
          <hr className="solid" style={{ margin: '0 0 10px 0' }}></hr>

          {editMode ?
            <Container>
              { hasAnyPerm(user, ["SystemAdminFullAccess"], true) ?
                <>
                  <Row className={classes.admin_pages__field_label}>
                    <Col>{t("I18N_Name")}: *</Col>
                    <Col>{t("I18N_Language")}: *</Col>
                    <Col>{t("I18N_Description")}: *</Col>
                    <Col>{t("I18N_Type")}: *</Col>
                  </Row>
                  <Row className={classes.admin_pages__field_value}>
                    <Col>
                      <input className={classes.admin_pages__field_editable} name="name" value={objAttrs.name} style={{width:'100%'}}
                        onChange={(e) => onAttrsInputChange(e)} />
                    </Col>
                    <Col>
                      <DropdownButton size="tiny_dropdown" variant="outline-secondary" id="dropdown-basic-button"
                        title={t("I18N_Lng_" + objAttrs.language)}
                        onSelect={onLanguageSelect}>
                        {languageList.map((item, index) => {
                          return (
                            <Dropdown.Item key={index} eventKey={item.key} className={classes.dropdown_item_wrap}>
                              {t("I18N_Lng_" + item.value)}
                            </Dropdown.Item>
                          );
                        })}
                      </DropdownButton>
                    </Col>
                    <Col>
                      <textarea className={classes.admin_pages__field_editable} name="description" value={objAttrs.description}
                                style={{width:'100%'}}
                                onChange={(e) => onAttrsInputChange(e)} />
                    </Col>
                    <Col>
                      <DropdownButton size="tiny_dropdown" variant="outline-secondary" id="dropdown-basic-button"
                        title={t("I18N_" + objAttrs.type)}
                        onSelect={onTypeSelect}>
                        {typeList.map((item, index) => {
                          return (
                            <Dropdown.Item key={index} eventKey={item.key} className={classes.dropdown_item_wrap}>
                              {t("I18N_" + item.value)}
                            </Dropdown.Item>
                          );
                        })}
                      </DropdownButton>
                    </Col>
                  </Row>
                </>
                :
                <>
                  <Row className={classes.admin_pages__field_label}>
                    <Col>{t("I18N_Name")}:</Col>
                    <Col>{t("I18N_Language")}:</Col>
                    <Col>{t("I18N_Description")}:</Col>
                    <Col>{t("I18N_Type")}:</Col>
                  </Row>
                  <Row className={classes.admin_pages__field_value}>
                    <Col style={{minWidth:'60px', flexWrap:'wrap', wordWrap:'break-word'}}>{objAttrs.name}</Col>
                    <Col>{t("I18N_Lng_" + objAttrs.language)}</Col>
                    <Col>{objAttrs.description}</Col>
                    <Col>{t("I18N_" + objAttrs.type)}</Col>
                  </Row>
                </>
              }

              <Row className={classes.admin_pages__field_label} style={{ marginTop: '10px' }}>
                <Col>{t("I18N_Subject")}: *</Col>
              </Row>
              <Row className={classes.admin_pages__field_value}>
                <Col>
                  <textarea className={classes.admin_pages__field_editable} name="subject" value={objAttrs.subject}
                    rows={1} style={{width:'100%', fontFamily:'Courier New'}}
                    onChange={(e) => onAttrsInputChange(e)} />
                </Col>
              </Row>

              <Row className={classes.admin_pages__field_label} style={{ marginTop: '10px' }}>
                <Col>
                  <div display='flex' style={{marginBottom:'5px'}}>
                    <span>{t("I18N_Body")}: *</span>
                    <span style={{marginLeft:'10px'}}>
                      <Button size="tiny" variant="outline-secondary"
                        onClick={togglePreview}>
                        { !previewMode ? t("I18N_Preview") : t("I18N_Close_preview")}
                      </Button>
                    </span>
                 </div>
                </Col>
              </Row>
              <Row className={classes.admin_pages__field_value}>
                <Col>
                  { !previewMode ?
                    <textarea className={classes.admin_pages__field_editable} name="body" value={objAttrs.body}
                      rows={19} style={{width:'100%', fontFamily:'Courier New'}}
                      onChange={(e) => onAttrsInputChange(e)} />
                    :
                    <div style={{padding:'15px', maxHeight:'360px', overflowY:'scroll', border: '1px solid lightgray', borderRadius:'5px',
                                 backgroundColor:'#FEFEFE'}} dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(objAttrs.body) }} />
                  }
                </Col>
              </Row>

              <Row className={classes.admin_pages__field_label} style={{marginTop:'10px', fontStyle:'italic'}}>
                <span>{t("I18N_Req_fields")}</span>
              </Row>

              <Row>
                <div display='flex' style={{marginTop:'15px'}}>
                  <Button size="tiny" variant="outline-secondary" className="mb-3"
                    style={{ float: 'left' }}
                    onClick={handleEditCancel}>
                    {t("I18N_Cancel")}
                  </Button>
                  <Button size="tiny" variant="outline-secondary" className="mb-3"
                    style={{ marginLeft: '15px' }}
                    onClick={handleEditSubmit}>
                    {t("I18N_Save")}
                  </Button>
                  <span className={classes.admin_pages__field_label} style={{marginLeft:'30px', color:'rgba(200,0,0)', verticalAlign:'top'}}>
                    {errMsg}
                  </span>
                </div>
              </Row>
            </Container>
            :
            <Container>
              <Row className={classes.admin_pages__field_label}>
                <Col>{t("I18N_Name")}:</Col>
                <Col>{t("I18N_Language")}:</Col>
                <Col>{t("I18N_Description")}:</Col>
                <Col>{t("I18N_Type")}:</Col>
              </Row>
              <Row className={classes.admin_pages__field_value}>
                <Col style={{minWidth:'60px', flexWrap:'wrap', wordWrap:'break-word'}}>{objAttrs.name}</Col>
                <Col>{t("I18N_Lng_" + objAttrs.language)}</Col>
                <Col>{objAttrs.description}</Col>
                <Col>{t("I18N_" + objAttrs.type)}</Col>
              </Row>

              <Row className={classes.admin_pages__field_label} style={{ marginTop: '10px' }}>
                <Col>{t("I18N_Subject")}:</Col>
              </Row>
              <Row className={classes.admin_pages__field_value}>
                <Col>
                  <textarea readOnly disabled rows={1} className={classes.admin_pages__field_editable} name="subject" value={objAttrs.subject}
                    style={{marginTop:'5px', width:'100%', fontFamily:'Courier New'}} />
                </Col>
              </Row>

              <Row className={classes.admin_pages__field_label} style={{ marginTop: '10px' }}>
                <Col>{t("I18N_Body")}:</Col>
              </Row>
              <Row className={classes.admin_pages__field_value}>
                <Col>
                  <textarea readOnly disabled rows={20} className={classes.admin_pages__field_editable} name="body" value={objAttrs.body}
                    style={{marginTop:'5px', width:'100%', fontFamily:'Courier New'}} />
                </Col>
              </Row>

              <Row className={classes.admin_pages__field_label} style={{ marginTop: '10px' }}>
                  <Col>{t("I18N_Updated_by")}:</Col>
                  <Col>{t("I18N_Time_updated")}:</Col>
                  <Col>{t("I18N_Created_by")}:</Col>
                  <Col>{t("I18N_Time_created")}:</Col>
              </Row>
              <Row className={classes.admin_pages__field_value}>
                <Col>{objAttrs.updaterUsername}</Col>
                <Col>{formatDate(objAttrs.timeUpdated)}</Col>
                <Col>{objAttrs.creatorUsername}</Col>
                <Col>{formatDate(objAttrs.timeCreated)}</Col>
              </Row>
            </Container>
          }
        </div>

        <hr className="solid" style={{ marginTop: '30px' }}></hr>

        <div className={classes.admin_pages__page_subtitle} style={{ marginTop: '15px' }}>
          <Form.Group as={Row} className="mb-2 mt-3">
            <div style={{ display: "flex" }}>
              <Button size="sm" variant="outline-secondary" className="mb-3"
                style={{ fontSize: '12px', fontWeight: '300' }}
                onClick={handleClose}>
                {t("I18N_Close")}
              </Button>
            </div>
{/*
              <span className={classes.admin_pages__field_label} style={{ color: 'rgba(200,0,0)', verticalAlign: 'top' }}>
                {updateMsgTmplErrMsg}
              </span>
*/}
          </Form.Group>
        </div>

      </Card>
    </>
  );
}

export default MsgTmplAdminDetailsView;
